import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import {
  SearchableCheckbox,
  DirtyFormAlert,
  Label,
  Modal,
  Notification,
} from 'shared/components';
import { createLicenseFeature } from 'licenses/actions';
import ProductFeaturesEditor from 'CreateOrderForm/components/SelectProductForm/components/ProductFeaturesEditor';
import { useTranslation } from 'react-i18next';
import { mapFeaturesToSelector } from 'shared/helpers';

const AddProductFeatureForm = ({
  closeCb,
  refetchLicense,
  license,
  existingLicenseFeatures,
}) => {
  const licenseProductFeatures = Array.isArray(get(license, 'product_features'))
    ? get(license, 'product_features')
    : [];
  const hasLegacyLicenseFeaturesShape = licenseProductFeatures.some((feature) =>
    Boolean(get(feature, 'product_feature.id')),
  );
  const legacyProductFeatures = Array.isArray(
    get(license, 'product.product_features'),
  )
    ? get(license, 'product.product_features')
    : [];
  const featuresList = hasLegacyLicenseFeaturesShape
    ? legacyProductFeatures
    : licenseProductFeatures;
  const { t } = useTranslation();
  const licenseID = get(license, 'id');

  const licenseFeatureIds = useMemo(
    () =>
      new Set(
        (existingLicenseFeatures.length
          ? existingLicenseFeatures
          : hasLegacyLicenseFeaturesShape
            ? licenseProductFeatures
            : []
        )
          .map((feature) => get(feature, 'product_feature.id'))
          .filter(Boolean),
      ),
    [
      existingLicenseFeatures,
      hasLegacyLicenseFeaturesShape,
      licenseProductFeatures,
    ],
  );

  const productFeatures = featuresList.filter(
    (feature) =>
      !licenseFeatureIds.has(
        get(feature, 'id') || get(feature, 'product_feature.id'),
      ),
  );

  const productFeaturesOptions = useMemo(
    () => mapFeaturesToSelector(productFeatures),
    [productFeatures],
  );

  const [isLoading, setLoading] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [isDirtyFormDisplayed, setDirtyFormDisplay] = useState(false);
  const [features, setFeatures] = useState([]);
  const [error, setError] = useState('');

  const selectedProductFeatureOptions = useMemo(
    () =>
      productFeaturesOptions.filter((option) =>
        features.some((f) => f.value === option.value),
      ),
    [features, productFeaturesOptions],
  );

  const handleClose = () => {
    if (!isDirty) {
      return closeCb();
    }
    return setDirtyFormDisplay(true);
  };

  const validateFeaturesConsumption = (values) => {
    if (!values.length) {
      setError(false);
      return true;
    }

    const hasError = values.some(
      (f) =>
        f.max_consumption_error ||
        f.max_overages_error ||
        f.expiry_date_error ||
        f.metadata_error,
    );
    if (hasError || error) {
      setError(true);
      return false;
    }
    setError(false);
    return true;
  };

  const handleSelection = (values) => {
    setDirty(true);
    if (!features.length) {
      setFeatures(values);
      validateFeaturesConsumption(values);
      return true;
    }
    const newSelectedList = values.map((v) => {
      const isSelected = features.find((sf) => sf.value === v.value);
      if (isSelected) {
        return isSelected;
      }
      return v;
    });
    setFeatures(newSelectedList);
    validateFeaturesConsumption(newSelectedList);
    return true;
  };

  const handleProductFeaturesChange = (values) => {
    return handleSelection(values);
  };

  const isFormValid = () => {
    const areProductFeaturesValid = validateFeaturesConsumption(features);
    return areProductFeaturesValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await isFormValid();
    if (!isValid || isLoading || error) {
      return false;
    }

    if (!isDirty || !features.length) {
      closeCb();
      return false;
    }

    setLoading(true);
    const selectedFeatures = features.map((f) => ({
      license: licenseID,
      product_feature: get(f, 'data.id'),
      max_consumption: Number(f.max_consumption),
      total_consumptions: 0,
      expiry_date: get(f, 'expiry_date', null),
      valid_duration: get(f, 'valid_duration', null),
      allow_overages: get(f, 'allow_overages', false),
      max_overages: get(f, 'max_overages', 0),
      reset_consumption: get(f, 'reset_consumption', false),
      consumption_period: get(f, 'consumption_period', 'monthly'),
      allow_unlimited_consumptions: get(
        f,
        'allow_unlimited_consumptions',
        false,
      ),
      allow_negative_consumptions: get(f, 'allow_negative_consumptions', false),
      metadata: get(f, 'metadata', ''),
      is_floating: get(f, 'is_floating'),
      is_floating_cloud: get(f, 'is_floating_cloud'),
      floating_users: get(f, 'floating_users'),
      floating_timeout: get(f, 'floating_timeout', 120),
      can_borrow: get(f, 'can_borrow', false),
      max_borrow_time: get(f, 'max_borrow_time', 0),
    }));

    try {
      await Promise.all(selectedFeatures.map((sf) => createLicenseFeature(sf)));
      refetchLicense();
      Notification(
        'success',
        t('Changes saved successfully'),
        t('Product features added to license'),
      );
      closeCb();
    } catch (err) {
      setLoading(false);
      Notification(
        'error',
        t('Your changes were not saved'),
        t('There was an error while saving your changes'),
      );
    }
    return true;
  };

  return (
    <Modal
      closeCb={handleClose}
      confirmCb={handleSubmit}
      disabled={isLoading}
      title={t('Add product features')}
      size="lg"
    >
      <form className="AddProductFeatureForm" onSubmit={handleSubmit}>
        <div className="ProductFeatures-selector">
          <Label
            text={t('Select product features')}
            inputId="product-features"
          />
          <SearchableCheckbox
            text={t('Features list')}
            options={productFeaturesOptions}
            value={selectedProductFeatureOptions}
            onChangeCallback={handleProductFeaturesChange}
            onMenuClose={() => {}}
          />
        </div>
        <div>
          <ProductFeaturesEditor
            selectedFeatures={features}
            onFeaturesChange={(data) => {
              setFeatures(data);
              validateFeaturesConsumption(data);
            }}
            disableFeatureExpiry={false}
            license={license}
          />
        </div>
      </form>
      {isDirtyFormDisplayed && (
        <DirtyFormAlert
          dirty={isDirty}
          closeAlert={() => setDirtyFormDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

AddProductFeatureForm.propTypes = {
  closeCb: PropTypes.func.isRequired,
  refetchLicense: PropTypes.func.isRequired,
  license: PropTypes.object.isRequired,
  existingLicenseFeatures: PropTypes.array,
};

AddProductFeatureForm.defaultProps = {
  existingLicenseFeatures: [],
};

export default AddProductFeatureForm;
